import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Search } from 'lucide-react';

interface AddressResult {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
  };
}

interface LocationData {
  address: string;
  city: string;
  zipCode: string;
  lat: number;
  lng: number;
}

interface LocationAutocompleteProps {
  onLocationSelect: (data: LocationData) => void;
  onInputChange?: (value: string) => void; // NOVO: Para capturar escrita manual
  initialValue?: string;
  placeholder?: string;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({ 
  onLocationSelect, 
  onInputChange,
  initialValue = '', 
  placeholder = 'Start typing address...' 
}) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<AddressResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  // Sincroniza valor inicial
  useEffect(() => {
    if (initialValue !== query) setQuery(initialValue);
  }, [initialValue]);

  // Pesquisa com Debounce
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      if (!isOpen && query === initialValue) return;

      setIsLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=pt&addressdetails=1&limit=5`;
        
        const response = await fetch(url, { headers: { 'Accept-Language': 'pt-PT,pt;q=0.9,en;q=0.8' } });
        
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
          setIsOpen(true);
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      } finally {
        setIsLoading(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSelect = (item: AddressResult) => {
    let street = item.address.road || item.display_name.split(',')[0];
    if (item.address.house_number) street += `, ${item.address.house_number}`;

    const city = item.address.city || item.address.town || item.address.village || item.address.municipality || '';
    const zip = item.address.postcode || '';

    const locationData: LocationData = {
      address: street,
      city: city,
      zipCode: zip,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon)
    };

    setQuery(street);
    setIsOpen(false);
    onLocationSelect(locationData);
    if (onInputChange) onInputChange(street); // Atualiza pai também
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {isLoading ? <Loader2 className="h-5 w-5 text-emerald-500 animate-spin" /> : <Search className="h-5 w-5 text-slate-400" />}
        </div>
        <input
          type="text"
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all bg-white text-slate-800 placeholder-slate-400 shadow-sm"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            setIsOpen(true);
            if (onInputChange) onInputChange(val); // IMPORTANTE: Atualiza o formulário enquanto escreves
          }}
          onFocus={() => query.length > 2 && setIsOpen(true)}
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
          <ul className="py-1">
            {suggestions.map((item, idx) => (
              <li 
                key={idx}
                onClick={() => handleSelect(item)}
                className="px-4 py-3 hover:bg-emerald-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 bg-slate-100 rounded-full group-hover:bg-emerald-200 transition-colors text-slate-500 group-hover:text-emerald-800">
                    <MapPin className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 leading-tight">
                       {item.address.road || item.display_name.split(',')[0]} 
                       {item.address.house_number ? `, ${item.address.house_number}` : ''}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {item.address.postcode} {item.address.city || item.address.town || item.address.municipality}, Portugal
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="bg-gray-50 px-3 py-1.5 border-t border-gray-100 flex justify-end">
             <span className="text-[10px] text-slate-400">Powered by OpenStreetMap</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;